import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from "react";

// Arrastrar un panel que ya tiene su lugar por CSS (p. ej. la tarjeta de la
// unidad arriba a la derecha): se guarda sólo el desplazamiento y se aplica con
// la propiedad `translate`, que se suma a `transform` sin pisarlo: las
// animaciones de entrada y de minimizar del panel (transform) siguen igual.

export type Offset = { x: number; y: number };
type Rect = { left: number; top: number; right: number; bottom: number };

/** Limita el desplazamiento para que el panel no salga del área (`area`, p. ej. el mapa). */
export function clampOffset(offset: Offset, panel: Rect, area: Rect, current: Offset): Offset {
  // `panel` es la posición en pantalla con el desplazamiento `current` ya aplicado.
  const baseLeft = panel.left - current.x;
  const baseTop = panel.top - current.y;
  const w = panel.right - panel.left;
  const h = panel.bottom - panel.top;
  const x = Math.min(area.right - w - baseLeft, Math.max(area.left - baseLeft, offset.x));
  const y = Math.min(area.bottom - h - baseTop, Math.max(area.top - baseTop, offset.y));
  return { x, y };
}

const read = (key: string): Offset => {
  try {
    const o = JSON.parse(localStorage.getItem(key) || "null");
    return o && Number.isFinite(o.x) && Number.isFinite(o.y) ? o : { x: 0, y: 0 };
  } catch {
    return { x: 0, y: 0 };
  }
};

/**
 * `enabled` en falso (p. ej. en el teléfono, donde la tarjeta es una hoja
 * abajo) deja el panel en su lugar y no responde al arrastre.
 * El desplazamiento se recuerda por navegador: es comodidad de quien mira.
 */
export function useDragOffset(storageKey: string, enabled: boolean, externalRef?: RefObject<HTMLDivElement | null>) {
  const ownRef = useRef<HTMLDivElement>(null);
  const ref = externalRef ?? ownRef;
  const [offset, setOffset] = useState<Offset>(() => read(storageKey));
  const drag = useRef<{ x: number; y: number; from: Offset; now: Offset } | null>(null);

  // Al montar (o al cambiar el tamaño de la ventana) se reacomoda dentro del área.
  const fit = useCallback(() => {
    const el = ref.current;
    const area = el?.offsetParent as HTMLElement | null;
    if (!el || !area || !enabled) return;
    setOffset((o) => clampOffset(o, el.getBoundingClientRect(), area.getBoundingClientRect(), o));
  }, [enabled]);
  useEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit]);

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (!enabled || (e.target as HTMLElement).closest("button, a, input")) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, from: offset, now: offset };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    const el = ref.current;
    const area = el?.offsetParent as HTMLElement | null;
    if (!d || !el || !area) return;
    const want = { x: d.from.x + e.clientX - d.x, y: d.from.y + e.clientY - d.y };
    d.now = clampOffset(want, el.getBoundingClientRect(), area.getBoundingClientRect(), d.now);
    // Directo al estilo: sin re-render de React por cada movimiento del puntero.
    el.style.translate = `${d.now.x}px ${d.now.y}px`;
  };
  const onPointerUp = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    setOffset(d.now);
    try {
      localStorage.setItem(storageKey, JSON.stringify(d.now));
    } catch {
      /* sin almacenamiento: vale sólo para esta sesión */
    }
  };

  return {
    ref,
    style: enabled && (offset.x || offset.y) ? { translate: `${offset.x}px ${offset.y}px` } : undefined,
    handlers: enabled ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } : {},
  };
}
